// src/services/listingsCountSignal.js
//
// "The number of published listings may have changed."
//
// CompanyContext reads GET /listings exactly once, on mount, and the provider
// wraps the whole /company/* subtree so it never remounts between company
// pages. Everything downstream of that read -- the topbar's "N Active Listings"
// badge above all -- therefore reports a mount-time snapshot for the rest of
// the session. Observed live: the badge said 12 while the dashboard, which
// refetches on every mount, correctly reported 0.
//
// The four roster presses that move the count already report themselves through
// CompanyListingsSurface's `onListingsChanged` prop, which works because the
// listings page owns both ends. This signal is for the writes that DO NOT have
// that luxury, because they happen on a different route from the badge:
//
//   - job feed connect / pull / disconnect / reconfigure (Settings). The
//     disconnect toast literally reads "Its imported roles were unpublished and
//     soft-deleted" while the badge went on showing the old number.
//   - bulk file import (a modal on the listings page whose success handler only
//     refetched facilities)
//   - facility delete, which cascades `deleted: true` onto every listing the
//     facility owned, and is reachable from Settings as well as from Listings
//
// THE SIGNAL IS HERE AND NOT IN A HOOK, for the same reason companyJourneyService
// puts its own signal in the service layer: all three of those writes are made
// by services, and a service that had to import React to announce its own write
// would drag React into modules that have no business with it. Listeners are a
// Set so a provider that mounts twice under StrictMode registers and drops
// cleanly.
//
// What this signal does NOT cover, and cannot: a feed poller importing roles
// server-side, an admin acting on the company's behalf, a second tab. Those
// never touch this bundle. CompanyContext's focus refetch is what answers those.

const listeners = new Set();

/**
 * Hear about writes that could move the published-listing count. Returns the
 * unsubscribe.
 *
 * A non-function is ignored rather than stored, so a caller that hands this an
 * undefined callback gets a working no-op instead of a crash on the next write.
 */
export const subscribeToListingsCountChanges = (listener) => {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

/**
 * A write landed that the count predicate could read differently now.
 *
 * Every listener runs inside its own try/catch. The write has ALREADY happened
 * by the time this is called, so a badge that fails to refresh is never worth
 * turning a completed disconnect, import or delete into a rejected one.
 */
export const notifyListingsCountChanged = () => {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch {
            /* a broken listener must never fail the write that fired it */
        }
    });
};
